import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trophy, ArrowLeft } from 'lucide-react';
import { getChildTheme } from '../childThemes';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const ChildCollectionPage = () => {
  const [child, setChild] = useState(null);
  const [theme, setTheme] = useState(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [collection, setCollection] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const childUser = localStorage.getItem('childUser');
    const childToken = localStorage.getItem('childToken');
    
    if (!childUser || !childToken) {
      navigate('/child/login');
      return;
    }
    
    // Set API token from childToken for authenticated requests
    api.setToken(childToken);
    
    const childData = JSON.parse(childUser);
    setChild(childData);
    const childTheme = getChildTheme(childData.theme || 'purple');
    setTheme(childTheme);
    
    // Load photo from localStorage immediately for instant display
    const cachedPhotoUrl = localStorage.getItem(`child_photo_${childData.id}`);
    if (cachedPhotoUrl && cachedPhotoUrl !== 'null') {
      setProfilePhoto(cachedPhotoUrl);
    }
    
    loadCollection(childData.id);
  }, [navigate]);

  const loadCollection = async (childId) => {
    try {
      setLoading(true);
      const purchases = await api.getRewardPurchases(childId);
      setCollection(purchases);
    } catch (error) {
      console.error('Error loading collection:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your collection. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!child || !theme || loading) return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
      <p className="text-xl text-slate-600">Loading collection...</p>
    </div>
  );

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.cardBg}`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${theme.headerBg} text-white p-6 shadow-lg`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/child/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            {/* Profile Photo */}
            <div
              className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-xl font-bold shadow-lg overflow-hidden cursor-pointer"
              style={{ color: child.profileColor }}
              onClick={() => navigate('/child/dashboard')}
            >
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                child.name.charAt(0)
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="bg-white/20 backdrop-blur-sm p-4 rounded-lg">
              <Trophy className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-4xl font-bold">My Collection</h1>
              <p className={`${theme.accentColor === 'purple-600' ? 'text-purple-100' : 'text-blue-100'}`}>
                Your earned stickers and pets
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {collection.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {collection.map((item) => (
              <Card key={item.id} className="overflow-hidden border-0 shadow-md bg-white">
                {/* Image */}
                <div className="aspect-square bg-gradient-to-br from-purple-50 to-pink-50 p-4 flex items-center justify-center">
                  {item.image_url ? (
                    <>
                      <img
                        src={item.image_url}
                        alt={item.reward_name}
                        className="w-full h-full object-contain"
                        data-testid={`collection-img-${item.id}`}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fb = e.currentTarget.nextElementSibling;
                          if (fb) fb.style.display = 'flex';
                        }}
                      />
                      <div className="text-6xl w-full h-full items-center justify-center" style={{ display: 'none' }}>
                        {item.reward_icon || '🎁'}
                      </div>
                    </>
                  ) : (
                    <div className="text-6xl">{item.reward_icon}</div>
                  )}
                </div>
                {/* Info */}
                <div className="p-3 text-center">
                  <h3 className="font-bold text-slate-900 text-sm line-clamp-1">{item.reward_name}</h3>
                  <p className="text-xs text-slate-500 mt-1 capitalize">{item.reward_type}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(item.purchased_at).toLocaleDateString()}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 border-0 shadow-lg bg-white text-center">
            <Trophy className="w-20 h-20 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">No Items Yet</h2>
            <p className="text-slate-600 mb-6">
              Visit the Rewards Store to purchase stickers and pets with your tokens!
            </p>
            <Button
              className={`bg-gradient-to-r ${theme.buttonPrimary}`}
              onClick={() => navigate('/child/rewards')}
            >
              Go to Rewards Store
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ChildCollectionPage;
